import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConversationMetaModel, DataViewEnum, ConversationDataModel, ColumnModel } from '../models';
import { RootState } from '../app/store';
import { setFormattedData } from '../helpers/helper';
import { formatUtcTimestamp, getUtcTimestampMs } from '../helpers/utcDateTimes';

export interface CommunicationsState {
  mediaType: string;
  dataView: DataViewEnum;

  data: ConversationDataModel;
}

const initialState: CommunicationsState = {
  mediaType: 'PhoneCall',
  dataView: DataViewEnum.List,

  data: {
    recordCount: 0,
    records: [],
    rawData: [],
    columns: []
  },
};

export const communicationsUniqueDataKey = "conversationMetaId";

const formatDuration = (value: any): string => {
  if (value === null || value === undefined) return 'n/a';
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) return String(value);
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(wholeSeconds / 60);
  const secs = wholeSeconds % 60;
  if (mins <= 0) return `${secs}s`;
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
};

const formatMoney = (value: any): string => {
  const amount = Number(value ?? 0);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(amount) ? amount : 0);
};

export const communicationsColumnConfig: Record<string, Partial<ColumnModel>> = {
  primaryKeyId: {
      width: 60,
      formatter: (record: ConversationMetaModel) => record.conversationMetaId,
  },
  customer: {
    width: 200,
    formatter: (record: ConversationMetaModel) => `${record.customerId} - ${record.firstName ?? ""} ${record.middleName ?? ""} ${record.lastName ?? ""}`,
    sortable: false,
  },
  duration: {
    header: 'Duration',
    width: 110,
    formatter: (record: ConversationMetaModel) => formatDuration(record.durationOfConversation),
    sortable: false,
  },
  totalCostOfCall: {
    header: 'Cost',
    width: 110,
    formatter: (record: ConversationMetaModel) => formatMoney(record.totalCostOfCall),
    sortable: true,
    sortKind: 'number',
    sortAccessor: (record: ConversationMetaModel) => Number(record.totalCostOfCall ?? 0),
  },
  // duration: {
  //   width: 130,
  //   formatter: (record: ConversationMetaModel) => `${Math.floor(record.durationOfConversation / 60)}m ${record.durationOfConversation % 60}s`,
  // },
  contactedOn: {
    width: 200,
    formatter: (r) => formatUtcTimestamp(r.contactedOn, 'DD/MM/YYYY, hh:mm:ss') || 'n/a',
    sortable: true,
    sortAccessor: (r) => getUtcTimestampMs(r.contactedOn, 0)
  },
  qualityScore: {
    header: 'Quality',
    width: 95,
    formatter: (record: ConversationMetaModel) => (record.qualityScore ?? 'n/a') as any,
    sortable: true,
    sortKind: 'number',
    sortAccessor: (r: any) => (r.qualityScore === null || r.qualityScore === undefined ? -1 : Number(r.qualityScore)),
  },
  sentimentScore: {
    header: 'Sentiment',
    width: 110,
    formatter: (record: ConversationMetaModel) => (record.sentimentScore ?? 'n/a') as any,
    sortable: true,
    sortKind: 'number',
    sortAccessor: (r: any) => (r.sentimentScore === null || r.sentimentScore === undefined ? -1 : Number(r.sentimentScore)),
  },
  needsAttention: {
    header: 'Needs Attention',
    width: 150,
    formatter: (record: ConversationMetaModel) => (record.needsAttention ? 'Yes' : 'No'),
    sortable: true,
    sortKind: 'string',
  },
  from: {
    width: 190,
    formatter: (record: ConversationMetaModel) => record.fromPhoneNumber
  },
  to: {
    width: 170,
    formatter: (record: ConversationMetaModel) => record.toPhoneNumber
  },
  mediaType: {
    width: 130,
    formatter: (record: ConversationMetaModel) => record.mediaType
  },
};
export const communicationsSlice = createSlice({
  name: 'communications',
  initialState,
  reducers: {
    setMediaType: (state, action: PayloadAction<string>) => {
      state.mediaType = action.payload;
      return state;
    },
    setDataView: (state, action: PayloadAction<DataViewEnum>) => {
      state.dataView = action.payload;
      return state;
    },
    setCommunicationsData: (state, action: PayloadAction<{ recordCount?: number, conversationMetadata: ConversationMetaModel[] }>) => {
      state.data = setFormattedData({ recordCount: action.payload ? action.payload.recordCount : undefined, rawData: action.payload ? action.payload.conversationMetadata : [] }, communicationsColumnConfig, communicationsUniqueDataKey);
      return state;
    },
    removeCommunication: (state, action: PayloadAction<number>) => {
      state.data.records = state.data.records.filter(record => record[communicationsUniqueDataKey] !== action.payload);
      state.data.rawData = state.data.rawData.filter(raw => raw[communicationsUniqueDataKey] !== action.payload);
      return state
    },
    setLogoutCommunications: (state) => {
      state = initialState;
      return state;
    },
  },
});

export const { setMediaType, setDataView, setCommunicationsData, removeCommunication, setLogoutCommunications } = communicationsSlice.actions;

export const selectMediaType = (state: RootState) => (state.communications.mediaType);

export default communicationsSlice.reducer;
